import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import { apiConfig } from '../config/api.config';
import { startLoading, stopLoading } from '../redux/slices/loadingSlice.ts';
import { getXsrfToken } from './base.api';

export interface SessionStatus {
    authenticated: boolean;
    email?: string;
    mfaRequired?: boolean;
}

/**
 * Shared onQueryStarted lifecycle — every endpoint in this file wires the same
 * start/stopLoading pair around queryFulfilled. Extracted here so a change to
 * that lifecycle happens in one place instead of four.
 */
async function withLoading(
    { dispatch, queryFulfilled, requestId }:
    { dispatch: any; queryFulfilled: Promise<unknown>; requestId: string },
) {
    dispatch(startLoading(requestId));
    try {
        await queryFulfilled.catch(() => { });
    } finally {
        dispatch(stopLoading(requestId));
    }
}

export const authApi = createApi({
    reducerPath: 'authApi',
    baseQuery: fetchBaseQuery({
        baseUrl: apiConfig.identityApiBaseUrl,
        credentials: 'include',
        prepareHeaders: (headers) => {
            const xsrfToken = getXsrfToken();
            if (xsrfToken) {
                headers.set('X-XSRF-TOKEN', xsrfToken);
            }
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getCsrfToken: builder.query<void, void>({
            query: () => '/auth/csrf-token',
        }),
        getSession: builder.query<SessionStatus, void>({
            query: () => '/auth/session',
            async onQueryStarted(_arg, api) { await withLoading(api); },
        }),
        login: builder.mutation<void, void>({
            query: () => ({
                url: '/auth/login',
                method: 'GET',
            }),
            async onQueryStarted(_arg, api) { await withLoading(api); },
        }),
        logout: builder.mutation<void, void>({
            query: () => ({
                url: '/auth/logout',
                method: 'GET',
            }),
            async onQueryStarted(_arg, api) { await withLoading(api); },
        }),
        keepAlive: builder.mutation<void, void>({
            query: () => ({
                url: '/auth/keep-alive',
                method: 'POST',
            }),
            async onQueryStarted(_arg, api) { await withLoading(api); },
        }),

    }),
});

export const { useGetSessionQuery, useLoginMutation, useLogoutMutation, useGetCsrfTokenQuery, useKeepAliveMutation } = authApi;
